import { skipToken } from '@reduxjs/toolkit/query'
import { useMemo } from 'react'

import { useOpenWindow, useWindow } from '@krp/client-runtime'
import {
  ONEC_WINDOWS,
  ProductsScreen,
  browseSubmitted,
  browseTextChanged,
  cameraScanAvailable,
  catalogRefreshRequested,
  makeSelectProductsVm,
  onecProductByBarcode,
  onecProductSearch,
  onecProductsWithPhotos,
  operationDismissed,
  productOperationResendRequested,
  productReloadRequested,
  selectBrowseIntent,
  type ProductsActions,
  normalizeRid,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'

/**
 * Binding окна «Товары 1С». rid — из контекста окна (адрес или `?w=`), не из
 * глобального «выбранного ресурса»: два окна двух баз живут рядом (D-064).
 * Args подписки и `select` совпадают — иначе селектор читал бы чужой ключ кэша.
 */
export function OneCProductsBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  // rid — в одном написании: ключи кэша, теги и черновики не расходятся по регистру GUID.
  const resourceId = normalizeRid(window.params.rid ?? '')
  const submitted = useAppSelector((state) => selectBrowseIntent(state.onec.products, resourceId)?.submitted ?? null)

  const textArgs = useMemo(
    () => (submitted?.mode === 'text' ? { resourceId, q: submitted.value } : skipToken),
    [resourceId, submitted],
  )
  const barcodeArgs = useMemo(
    () => (submitted?.mode === 'barcode' ? { resourceId, barcode: submitted.value } : skipToken),
    [resourceId, submitted],
  )
  onecProductSearch.useQuerySubscription(textArgs)
  onecProductByBarcode.useQuerySubscription(barcodeArgs)

  const selectFound = useMemo(() => {
    const selectText = onecProductSearch.select(textArgs)
    const selectBarcode = onecProductByBarcode.select(barcodeArgs)
    return (state: RootState) => (barcodeArgs !== skipToken ? selectBarcode(state) : selectText(state))
  }, [textArgs, barcodeArgs])
  const found = useAppSelector(selectFound)

  // Наличие фото — по найденным ключам этой же базы и той же ревизии.
  const photoArgs = useMemo(() => {
    const data = found.data
    if (data === undefined || data.data.length === 0) return skipToken
    const refKeys = [...new Set(data.data.map((product) => product.refKey.toLowerCase()))].sort()
    return { resourceId, refKeys, expectedConfigurationVersion: data.configurationVersion }
  }, [found.data, resourceId])
  onecProductsWithPhotos.useQuerySubscription(photoArgs)

  const selectVm = useMemo(() => {
    const selectPackageVm = makeSelectProductsVm()
    const selectPhotos = onecProductsWithPhotos.select(photoArgs)
    const camera = cameraScanAvailable()
    return (state: RootState) =>
      selectPackageVm({
        resourceId,
        intent: selectBrowseIntent(state.onec.products, resourceId),
        query: selectFound(state),
        photos: photoArgs === skipToken ? null : selectPhotos(state),
        cameraScanAvailable: camera,
        operations: state.onec.productOperations,
      })
  }, [photoArgs, resourceId, selectFound])
  const vm = useAppSelector(selectVm)

  const actions = useMemo<ProductsActions>(
    () => ({
      textChanged: (text) => dispatch(browseTextChanged({ resourceId, text })),
      searchSubmitted: () => dispatch(browseSubmitted({ resourceId, mode: 'text', value: vm.text })),
      barcodeSubmitted: (barcode) => dispatch(browseSubmitted({ resourceId, mode: 'barcode', value: barcode })),
      cleared: () => dispatch(browseSubmitted({ resourceId, mode: 'text', value: '' })),
      reload: () => dispatch(productReloadRequested({ resourceId, refKey: null })),
      refreshCatalog: () => dispatch(catalogRefreshRequested({ resourceId })),
      resendOperation: (key) => dispatch(productOperationResendRequested({ key })),
      dismissOperation: (key) => dispatch(operationDismissed({ key })),
      open: (refKey) => openWindow(ONEC_WINDOWS.product, { rid: resourceId, key: refKey }),
      create: () => openWindow(ONEC_WINDOWS.productCreate, { rid: resourceId }),
    }),
    [dispatch, openWindow, resourceId, vm.text],
  )

  return <ProductsScreen vm={vm} actions={actions} />
}
